import { useState, useRef, useEffect } from "react";
import { innerGrade } from "../../utils/piIduData";
import { clientPath } from "../../config/key";
import { Button } from "react-bootstrap";

const InnerGradeComponent = ({
  step,
  setStep,
  pricestepData,
  setPricestepData,
  getValueByField,
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const gradeRefs = useRef([]);
  const hasInitialFocus = useRef(false);
  const containerRef = useRef(null);
  
  const { color, saturation, colorInnerGrade } = pricestepData;
  const currentGrade = innerGrade[color]?.[saturation] || [];
  
  // Convert "4+" to 4 for comparison (keeping original logic)
  const activeIndex = colorInnerGrade === "4+" ? 4 : parseInt(colorInnerGrade) - 1;

  // Set initial focus when component mounts or when activeIndex changes
  useEffect(() => {
    if (step === 2) {
      if (activeIndex >= 0 && activeIndex < currentGrade.length) {
        setFocusedIndex(activeIndex);
      } else {
        setFocusedIndex(0);
      }
    }
  }, [step, activeIndex, currentGrade.length]);

  // Focus the appropriate element when focusedIndex changes
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < currentGrade.length) {
      gradeRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, currentGrade.length]);

  // Handle initial focus when component receives focus via tab
  useEffect(() => {
    const handleFocusIn = (e) => {
      if (containerRef.current?.contains(e.target) && !hasInitialFocus.current && step === 2) {
        hasInitialFocus.current = true;
        
        if (colorInnerGrade) {
          const activeIdx = colorInnerGrade === "4+" ? 4 : parseInt(colorInnerGrade) - 1;
          if (activeIdx >= 0 && activeIdx < currentGrade.length) {
            setFocusedIndex(activeIdx);
          }
        } else {
          setFocusedIndex(0);
        }
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [colorInnerGrade, currentGrade.length, step]);

  if (step !== 2) return null;

  const handleKeyDown = (e, index) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        setFocusedIndex(prev => {
          const nextIndex = Math.min(prev + 1, currentGrade.length - 1);
          return nextIndex;
        });
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedIndex(prev => {
          const prevIndex = Math.max(prev - 1, 0);
          return prevIndex;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectGrade(index);
        setTimeout(() => {
          const nextField = document.querySelector(`[data-field="calculate-price"]`);
          if (nextField) {
            nextField.focus();
          }
        }, 50);
        break;
      case 'Tab':
        if (!e.shiftKey && index === currentGrade.length - 1) {
          e.preventDefault();
          const nextField = document.querySelector(`[data-field="calculate-price"]`);
          if (nextField) nextField.focus();
        } else if (e.shiftKey && index === 0) {
          setFocusedIndex(-1);
        }
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(currentGrade.length - 1);
        break;
      default:
        break;
    }
  };

  const selectGrade = (index) => {
    // Keep the exact same 4+ logic as before
    // console.log("index????????", index);
    setPricestepData(prev => ({
      ...prev,
      colorInnerGrade: pricestepData?.saturation === "Fancy Deep" 
        ? "4" 
        : index === 4 ? "4+" : `${index + 1}`
    }));
  };

  return (
    <div 
      className="idu-section-container" 
      role="group" 
      aria-labelledby="inner-grade-label"
      ref={containerRef}
      tabIndex="-1"
    >
      <div className="idu-top-section">
        <h5 id="inner-grade-label">inner-grade</h5>
        <p>
          Defines the richness of color within the defined saturation.{" "}
          <span 
            onClick={() => getValueByField("color Inner-Grade")}
            // role="button"
            // tabIndex="0"
            onKeyDown={(e) => e.key === 'Enter' && getValueByField("color Inner-Grade")}
          >
            Read more…
          </span>
        </p>
      </div>
      
      <div className="idu-main-section">
        <div className="idu-undertone-arrow-section"></div>
        <div 
          className="idu-image-box"
          role="radiogroup"
          aria-label="Inner grade selection"
        >
          {currentGrade.map(({ image, bg }, index) => {
            const gradeValue = index === 4 ? "4+" : `${index + 1}`;
            // console.log("gradeValue", gradeValue);
            // console.log("colorInnerGrade", colorInnerGrade);
            // console.log("pricestepData?.saturation", pricestepData?.saturation);
            // Keep the exact same 4+ logic as before
            const isActive = (colorInnerGrade === "4+" ? "4+" : colorInnerGrade) === 
                           (pricestepData?.saturation === "Fancy Deep" ? "4" : gradeValue);
            // console.log("isActive", isActive);
            
            return (
              <div className="idu-image-container" key={index}>
                <Button
                  ref={el => gradeRefs.current[index] = el}
                  className={`idu-image ${isActive ? "active-image" : ""}`}
                  data-field={`inner-grade-${index}`}
                  onClick={() => selectGrade(index)}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onFocus={() => {
                    setFocusedIndex(index); 
                    setHoverIndex(index)
                  }}
                  tabIndex={focusedIndex === index ? 0 : -1}
                  role="radio"
                  aria-checked={isActive}
                  aria-label={`Inner grade ${gradeValue}`}
                >
                  <img
                    src={`${clientPath}/pi-idu-images/${color.toLowerCase()}/${color.toLowerCase()}-${image}`}
                    alt=""
                    aria-hidden="true"
                  />
                </Button>
                <p
                  style={{
                    backgroundColor: bg,
                    color: color === "Yellow" ? "#5C6DA4" : "#fff",
                  }}
                  onClick={() => selectGrade(index)}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  tabIndex="-1"
                >
                  {pricestepData?.saturation === "Fancy Deep" ? "4" : gradeValue}
                </p>
              </div>
            );
          })}
        </div>

        <div className="idu-text-box">
          <p>{currentGrade[hoverIndex ?? activeIndex]?.text}</p>
        </div>
      </div>

      <div className="idu-bottom-section">
        {Number(colorInnerGrade === "4+" ? 5 : colorInnerGrade) > 2 &&
          pricestepData?.saturation !== "Fancy Deep" && (
            <h6>
              <img
                src={`${clientPath}/icons/idu_info.svg`}
                height="21px"
                width="21px"
                alt="alert"
              />
              Overvaluing Visual Features Leads to Wrong Pricing!
            </h6>
          )}
      </div>
    </div>
  );
};

export default InnerGradeComponent;